import { useLang } from "@/context/LanguageContext";
import { team } from "@/data/team";
import { projects } from "@/data/projects";
import { awards } from "@/data/awards";
import {
  Mail,
  Phone,
  MapPin,
  Globe,
  Lightbulb,
  PenLine,
  ClipboardList,
  Clapperboard,
  Wand2,
} from "lucide-react";

/**
 * StudioProfilePdf
 * -------------------------------------------------------------
 * A hidden, print-friendly Studio Profile at `/studio-profile-pdf`.
 * Each section is sized to a portrait A4 sheet (210mm × 297mm) so it
 * paginates cleanly when the user uses "Save as PDF" from the browser.
 *
 * Print rules live inside a <style> tag so we don't touch global CSS.
 * The main Layout's navbar / footer are hidden at print via body[data-print].
 */

const methodologySteps = [
  { icon: Lightbulb, num: "01", title: "الفكرة والاختيار", en: "Idea & Selection" },
  { icon: PenLine, num: "02", title: "السكربت والـ Storyboard", en: "Script & Storyboard" },
  { icon: ClipboardList, num: "03", title: "التخطيط والإعداد", en: "Pre-Production" },
  { icon: Clapperboard, num: "04", title: "التصوير", en: "Production" },
  { icon: Wand2, num: "05", title: "المونتاج والمعايرة", en: "Post-Production" },
];

const StudioProfilePdf = ({ forceLang }) => {
  const ctx = useLang();
  const lang = forceLang || ctx.lang;
  const isAr = lang === "ar";

  // Signal the layout to hide chrome for this route
  if (typeof document !== "undefined") {
    document.body.setAttribute("data-print", "true");
  }

  const lead = team.find((m) => m.lead) || team[0];
  const rest = team.filter((m) => !m.lead);
  const featured = projects.slice(0, 4);

  return (
    <div className="pdf-root bg-white text-black" data-testid="studio-profile-pdf" data-lang={lang}>
      <style>{`
        /* ---------- SCREEN + PRINT SHARED PDF STYLES ---------- */
        .pdf-root {
          direction: ${isAr ? "rtl" : "ltr"};
          font-family: ${isAr ? "'Aref Ruqaa', 'Playfair Display', serif" : "'Playfair Display', serif"};
          color: #0A0A0C;
        }
        .pdf-page {
          width: 210mm;
          min-height: 297mm;
          max-height: 297mm;
          margin: 8mm auto;
          padding: 16mm 14mm;
          background: #0A0A0C;
          color: #ffffff;
          box-shadow: 0 20px 60px rgba(0,0,0,0.2);
          position: relative;
          overflow: hidden;
          page-break-after: always;
          break-after: page;
          page-break-inside: avoid;
          break-inside: avoid;
        }
        .pdf-page.light {
          background: #ffffff;
          color: #0A0A0C;
        }
        .pdf-page:last-child { page-break-after: auto; }
        .gold { color: #E6A95B; }
        .gold-bg { background: #E6A95B; }
        .gold-border { border-color: #E6A95B; }
        .pdf-h1 { font-size: 54pt; font-weight: 900; line-height: 1.08; letter-spacing: -0.5px; }
        .pdf-h2 { font-size: 32pt; font-weight: 800; line-height: 1.15; }
        .pdf-h3 { font-size: 20pt; font-weight: 700; line-height: 1.3; }
        .pdf-eyebrow {
          font-size: 12pt;
          letter-spacing: 5px;
          text-transform: uppercase;
          color: #E6A95B;
          font-weight: 700;
        }
        .pdf-body { font-size: 14pt; line-height: 1.95; }
        .pdf-small { font-size: 11pt; line-height: 1.7; color: rgba(255,255,255,0.8); }
        .pdf-section-h {
          font-size: 48pt;
          font-weight: 900;
          line-height: 1.1;
          letter-spacing: -0.5px;
          color: #E6A95B;
        }
        .pdf-center { text-align: center; }

        /* ============ ENGLISH DOWNSCALE ============
           English text is naturally longer than Arabic. Scale down font-sizes,
           padding and margins so each section still fits inside a single A4. */
        .pdf-root[data-lang="en"] .pdf-page { padding: 12mm 12mm; }
        .pdf-root[data-lang="en"] .pdf-h1 { font-size: 42pt; line-height: 1.05; }
        .pdf-root[data-lang="en"] .pdf-h2 { font-size: 26pt; }
        .pdf-root[data-lang="en"] .pdf-h3 { font-size: 16pt; }
        .pdf-root[data-lang="en"] .pdf-section-h { font-size: 38pt !important; line-height: 1.08; }
        .pdf-root[data-lang="en"] .pdf-eyebrow { font-size: 10pt; letter-spacing: 4px; }
        .pdf-root[data-lang="en"] .pdf-body { font-size: 11.5pt; line-height: 1.65; }
        .pdf-root[data-lang="en"] .pdf-small { font-size: 10pt; line-height: 1.55; }
        /* Force inline pt values back down (inline style would win otherwise) */
        .pdf-root[data-lang="en"] [style*="fontSize"] {}
        .pdf-root[data-lang="en"] .en-shrink-lg { font-size: 32pt !important; line-height: 1.1 !important; }
        .pdf-root[data-lang="en"] .en-shrink-md { font-size: 15pt !important; }
        .pdf-root[data-lang="en"] .en-shrink-sm { font-size: 13pt !important; }
        .pdf-root[data-lang="en"] .en-shrink-body { font-size: 11pt !important; line-height: 1.6 !important; }
        .pdf-root[data-lang="en"] .en-tight-space > * + * { margin-top: 10px !important; }
        .pdf-root[data-lang="en"] .mt-10 { margin-top: 20px !important; }
        .pdf-root[data-lang="en"] .mt-8 { margin-top: 16px !important; }
        .pdf-root[data-lang="en"] .mt-6 { margin-top: 12px !important; }
        .pdf-root[data-lang="en"] .space-y-5 > * + * { margin-top: 10px !important; }
        .pdf-root[data-lang="en"] .space-y-6 > * + * { margin-top: 12px !important; }

        /* ---------- PRINT-SPECIFIC ---------- */
        @media print {
          @page {
            size: A4 portrait;
            margin: 0;
          }
          html, body {
            background: #0A0A0C !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          /* Hide site chrome (nav, footer, ad slot, toaster) */
          body > div > div > nav,
          nav[data-testid="site-navbar"],
          footer,
          [data-testid="site-footer"],
          [data-testid="ad-slot"],
          .sonner-toaster,
          [class*="sonner"] {
            display: none !important;
          }
          .pdf-page {
            width: 210mm;
            min-height: 297mm;
            margin: 0;
            box-shadow: none !important;
            page-break-after: always;
          }
          .pdf-page:last-child { page-break-after: auto; }
          .no-print { display: none !important; }
        }
      `}</style>

      {/* Screen-only helper toolbar */}
      <div className="no-print sticky top-0 z-50 bg-[#0A0A0C] border-b border-white/10 py-3 px-6 flex items-center justify-between text-white">
        <div className="text-sm">
          <span className="gold font-semibold">ARTLENSS</span> · Studio Profile · Print Preview
        </div>
        <button
          onClick={() => window.print()}
          data-testid="pdf-print-btn"
          className="px-4 py-2 rounded-full bg-[#E6A95B] text-[#0A0A0C] text-sm font-bold hover:brightness-110"
        >
          {isAr ? "طباعة / حفظ PDF" : "Print / Save as PDF"}
        </button>
      </div>

      {/* ============ PAGE 1 — COVER ============ */}
      <section className="pdf-page flex flex-col items-center justify-between text-center" data-testid="pdf-page-1">
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: "url(/covers/hidden-red-sea.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,12,0.85) 0%, rgba(10,10,12,0.7) 40%, rgba(10,10,12,0.95) 100%)" }} />
        <div className="relative w-full pt-4">
          <div className="pdf-eyebrow">Cinematic Studio · Jeddah, KSA</div>
        </div>

        <div className="relative flex flex-col items-center gap-8">
          <img src="/brand/artlens-logo.png" alt="ARTLENSS" style={{ width: 220, height: "auto", filter: "drop-shadow(0 10px 40px rgba(230,169,91,0.5))" }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <h1
            className="pdf-h1"
            style={{
              background: "linear-gradient(135deg, #F6D28C 0%, #E6A95B 55%, #B7822F 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {isAr ? "الملف التعريفي للإنتاج السينمائي" : "Cinematic Production — Studio Profile"}
          </h1>
          <p className="pdf-small en-shrink-md" style={{ color: "rgba(255,255,255,0.9)", maxWidth: 560, fontSize: "18pt", lineHeight: 1.6, fontWeight: 500 }}>
            {isAr
              ? "خمسٌ وعشرون سنة من العطاء السينمائي بروح سعودية معاصرة."
              : "Twenty-five years of cinematic craft with a contemporary Saudi soul."}
          </p>
        </div>

        <div className="relative w-full pb-2 flex items-center justify-between pdf-small">
          <span>artlenss.com</span>
          <span>info@artlenss.com</span>
        </div>
      </section>

      {/* ============ PAGE 2 — DIRECTOR'S NOTE ============ */}
      <section className="pdf-page" data-testid="pdf-page-2">
        <div className="pdf-eyebrow pdf-center">{isAr ? "كلمة المخرج" : "Director's Note"}</div>
        <h2 className="pdf-section-h pdf-center mt-4" style={{ fontSize: "52pt" }}>
          {isAr ? "من العدسة… إلى الرؤية" : "From the Lens… to the Vision"}
        </h2>
        <div className="mt-10 grid grid-cols-12 gap-8 items-start">
          <div className="col-span-5 flex items-start justify-center">
            <div className="w-full rounded-lg overflow-hidden border-2 gold-border" style={{ aspectRatio: "3/4", background: "#0d0d10" }}>
              <img src={lead.image} alt={lead.name[lang]} className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="col-span-7 space-y-5 pdf-body en-shrink-body en-tight-space" style={{ fontSize: "15pt", lineHeight: 2 }}>
            <p>
              {isAr
                ? "أهلاً بكم في ARTLENSS. أنا هاني حجازي، مخرجٌ ومدير إبداعي أعيش منذ ربع قرن حكاية واحدة: كيف نصنع من الصورة صوتاً، ومن اللقطة موقفاً، ومن الفيلم شهادةَ زمن."
                : "Welcome to ARTLENSS. I'm Hani Hijazi — a director and creative lead who has lived, for a quarter-century, a single story: how we turn an image into a voice, a frame into a stance, and a film into a testimony of its time."}
            </p>
            <p>
              {isAr
                ? "منذ أن انطلقت رؤية المملكة 2030، تغيّرت لغة الإنتاج السعودي. لم تعد الكاميرا مجرّد أداة تسجيل، بل أداةَ تعبير عن هويّة وطنٍ يتجدّد. ونحن في ARTLENSS نؤمن أن كل كادر موقف، وكل قصّة تنمية، وكل فيلم استثمارٌ في الذاكرة الوطنية."
                : "Since Vision 2030 launched, the language of Saudi production has changed. The camera is no longer a tool of documentation — it has become a tool of identity for a nation renewing itself. At ARTLENSS, every frame is a statement, every story is development, every film is an investment in the national memory."}
            </p>
            <p>
              {isAr
                ? "نحن لا نُنتج محتوى فحسب، بل نبني أرشيفاً بصريّاً لجيلٍ سعوديٍّ جديد. جيلٌ يرى نفسه على الشاشة كما هو: طموحاً، متجذّراً، ومنفتحاً على العالم."
                : "We don't produce content — we build a visual archive for a new generation of Saudis: ambitious, rooted, and open to the world."}
            </p>
            <div
              className="mt-8 pt-5"
              style={{
                borderTop: "1px solid #E6A95B",
                textAlign: "left",
                direction: "ltr",
              }}
            >
              <div className="gold en-shrink-sm" style={{ fontSize: "18pt", fontWeight: 800, letterSpacing: 0.5 }}>
                {isAr ? "م/ هاني حجازي" : "Eng. Hani Hijazi"}
              </div>
              <div className="pdf-small" style={{ fontSize: "11pt" }}>{lead.role[lang]}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PAGE 3 — TEAM ============ */}
      <section className="pdf-page" data-testid="pdf-page-3">
        <div className="pdf-eyebrow pdf-center">{isAr ? "فريق العمل" : "The Crew"}</div>
        <h2 className="pdf-section-h pdf-center mt-4" style={{ fontSize: "48pt" }}>
          {isAr ? "طاقمٌ مُختار بعناية" : "A hand-picked crew"}
        </h2>
        <div className="mt-10 grid grid-cols-12 gap-6">
          {/* Lead large */}
          <div className="col-span-7">
            <div className="rounded-lg overflow-hidden border-2 gold-border" style={{ aspectRatio: "4/5", background: "#0d0d10" }}>
              <img src={lead.image} alt={lead.name[lang]} className="w-full h-full object-cover" />
            </div>
            <div className="mt-3">
              <div className="gold pdf-eyebrow">{lead.role[lang]}</div>
              <div style={{ fontSize: "18pt", fontWeight: 800, marginTop: 4 }}>{lead.name[lang]}</div>
            </div>
          </div>
          {/* Rest in 2×2 */}
          <div className="col-span-5 grid grid-cols-2 gap-4 content-center">
            {rest.map((m) => (
              <div key={m.name.en} className="flex flex-col items-center text-center">
                <div className="w-full rounded-md overflow-hidden border gold-border" style={{ aspectRatio: "3/4", background: "#0d0d10", borderWidth: 1 }}>
                  <img src={m.image} alt={m.name[lang]} className="w-full h-full object-cover" />
                </div>
                <div style={{ marginTop: 8, fontSize: "10pt", fontWeight: 700 }}>{m.name[lang]}</div>
                <div className="pdf-small" style={{ fontSize: "8pt", marginTop: 2 }}>{m.role[lang]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PAGE 4 — SELECTED WORK ============ */}
      <section className="pdf-page" data-testid="pdf-page-4">
        <div className="pdf-eyebrow pdf-center">{isAr ? "مختارات من أعمالنا" : "Selected Work"}</div>
        <h2 className="pdf-section-h pdf-center mt-4" style={{ fontSize: "44pt" }}>
          {isAr ? "أربعة أعمال… بلغات ثلاث للسينما" : "Four films · three languages of cinema"}
        </h2>
        <div className="mt-8 grid grid-cols-2 gap-5">
          {featured.map((p) => (
            <div key={p.id} className="flex flex-col rounded-lg overflow-hidden border gold-border" style={{ borderWidth: 1 }}>
              <div style={{ aspectRatio: "16/10", overflow: "hidden", background: "#0d0d10" }}>
                <img src={p.image} alt={p.title[lang]} className="w-full h-full object-cover" />
              </div>
              <div className="p-4 flex-1 flex flex-col text-center">
                <div className="gold pdf-eyebrow" style={{ fontSize: "9pt", letterSpacing: 3 }}>{p.category} · {p.year}</div>
                <div style={{ fontSize: "17pt", fontWeight: 900, marginTop: 8, lineHeight: 1.2 }}>{p.title[lang]}</div>
                <div className="pdf-small" style={{ marginTop: 8, fontSize: "10.5pt" }}>{p.short[lang]}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ PAGE 5 — METHODOLOGY ============ */}
      <section className="pdf-page" data-testid="pdf-page-5">
        <div className="pdf-eyebrow pdf-center">{isAr ? "منهجيتنا" : "Our Methodology"}</div>
        <h2 className="pdf-section-h pdf-center mt-4" style={{ fontSize: "44pt" }}>
          {isAr ? "كيف نصنع الأعمال في ARTLENSS" : "How we craft works at ARTLENSS"}
        </h2>
        <p className="pdf-body pdf-center mt-6" style={{ maxWidth: "85%", margin: "24px auto 0", fontSize: "14pt" }}>
          {isAr
            ? "خط إنتاج منضبط من خمس مراحل يُحوّل الأفكار الخام إلى سينما — كل مشروع يمرّ بها كاملة، بلا اختصارات."
            : "A disciplined, five-step pipeline that turns raw ideas into cinema. Every project passes through it, no shortcuts."}
        </p>
        <div className="mt-10 space-y-6 en-tight-space">
          {methodologySteps.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.num} className="flex items-start gap-6">
                <div className="gold-bg rounded-full flex items-center justify-center shrink-0" style={{ width: 72, height: 72, color: "#0A0A0C" }}>
                  <Icon size={34} />
                </div>
                <div className="flex-1 pt-2">
                  <div className="flex items-baseline gap-5">
                    <span className="gold en-shrink-lg" style={{ fontSize: "30pt", fontWeight: 900 }}>{s.num}</span>
                    <span className="en-shrink-sm" style={{ fontSize: "22pt", fontWeight: 800 }}>{isAr ? s.title : s.en}</span>
                  </div>
                  <div className="pdf-small mt-2" style={{ fontSize: "12pt", lineHeight: 1.75 }}>
                    {isAr
                      ? "مرحلة سينمائية دقيقة، مخطّطة، وموثّقة — لضمان قرارٍ إبداعيٍّ خلف كل كادر."
                      : "A precise, planned, documented stage — ensuring a creative decision behind every frame."}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ============ PAGE 6 — AWARDS ============ */}
      <section className="pdf-page" data-testid="pdf-page-6">
        <div className="pdf-eyebrow pdf-center">{isAr ? "الجوائز والتقدير" : "Awards & Recognition"}</div>
        <h2 className="pdf-section-h pdf-center mt-4" style={{ fontSize: "44pt" }}>
          {isAr ? "خمسٌ وعشرون سنة… محفورة بالذهب" : "Twenty-five years — engraved in gold"}
        </h2>
        <div className="mt-10 grid grid-cols-4 gap-4">
          {awards.map((a) => (
            <div key={a.id} className="rounded-lg overflow-hidden border gold-border p-3 flex flex-col" style={{ borderWidth: 1, background: "#0d0d10" }}>
              <div style={{ aspectRatio: "3/4", overflow: "hidden", background: "#000" }} className="flex items-center justify-center rounded-md">
                <img src={a.image} alt={a.title[lang]} className="w-full h-full object-contain" />
              </div>
              <div className="gold pdf-eyebrow mt-3" style={{ fontSize: "8pt", letterSpacing: 2 }}>{a.issuer[lang].slice(0, 42)}</div>
              <div style={{ fontSize: "11pt", fontWeight: 800, marginTop: 6, lineHeight: 1.3 }}>{a.title[lang]}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ PAGE 7 — CONTACT ============ */}
      <section className="pdf-page flex flex-col justify-between" data-testid="pdf-page-7">
        <div>
          <div className="pdf-eyebrow pdf-center">{isAr ? "لنبدأ الحكاية" : "Let's begin the story"}</div>
          <h2 className="pdf-section-h pdf-center mt-6" style={{ fontSize: "56pt", lineHeight: 1.15 }}>
            {isAr ? "لِنكتب معاً الفصل التالي." : "Let's write the next chapter — together."}
          </h2>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <ContactBlock icon={<Mail size={26} />} label={isAr ? "البريد الإلكتروني" : "Email"} value="info@artlenss.com" />
          <ContactBlock icon={<Phone size={26} />} label={isAr ? "الجوال" : "Mobile"} value="966 55 414 2007" />
          <ContactBlock icon={<MapPin size={26} />} label={isAr ? "الموقع" : "Location"} value={isAr ? "جدة · المملكة العربية السعودية" : "Jeddah · Saudi Arabia"} />
          <ContactBlock icon={<Globe size={26} />} label={isAr ? "الموقع الإلكتروني" : "Website"} value="artlenss.com" />
        </div>

        <div className="pdf-small text-center pt-6 border-t gold-border" style={{ borderTopWidth: 1, fontSize: "10pt" }}>
          © {new Date().getFullYear()} ARTLENSS · Cinematic Production Studio · Jeddah, KSA
        </div>
      </section>
    </div>
  );
};

const ContactBlock = ({ icon, label, value }) => (
  <div className="rounded-lg border gold-border p-6" style={{ borderWidth: 1, background: "#0d0d10" }}>
    <div className="flex items-center gap-4">
      <div className="gold-bg rounded-full flex items-center justify-center shrink-0" style={{ width: 52, height: 52, color: "#0A0A0C" }}>
        {icon}
      </div>
      <div>
        <div className="gold pdf-eyebrow" style={{ fontSize: "10pt", letterSpacing: 3 }}>{label}</div>
        <div style={{ fontSize: "17pt", fontWeight: 800, marginTop: 4, direction: "ltr", textAlign: "start" }}>{value}</div>
      </div>
    </div>
  </div>
);

export default StudioProfilePdf;
